const isValid = require('date-fns/is_valid')
const isAfter = require('date-fns/is_after')
const isBefore = require('date-fns/is_before')
const isSameDay = require('date-fns/is_same_day')
const endOfDay = require('date-fns/end_of_day')
const startOfDay = require('date-fns/start_of_day')
const format = require('date-fns/format')
const maskFromString = require('./mask-from-string')
const PLACEHOLDERS = require('../constants/date-placeholders')
const isObject = require('./is-object')
const pipe = require('./pipe')
const checkRequired = require('./check-required')
const forwardError = require('./forward-error')
const ERRORS = require('../constants/error-types')
var FORMATS = require('../constants/date-formats')
var ensureDateString = require('./ensure-date-string')

const patterns = {
  default: /^\d{2}([\/\-])\d{2}([\/\-])\d{4}$/,
  formatted: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,
  iso: /^\d{4}-\d{2}-\d{2}$/,
}

function isValidFormat(pattern) {
  let regex = pattern || patterns.default
  return value => regex.test(value) || patterns.formatted.test(value)
}

function isValidDate(data, pattern, pipeformat) {
  if (data.pattern) {
    return data.pattern.test(
      isValidCustomDateFormat(data.value, data.customFormat)
    ) ||
      data.pattern.test(data.value) ||
      patterns.formatted.test(data.value)
      ? ''
      : ERRORS.invalid
  }

  let testFormat = isValidFormat(pattern || patterns.default)
  let date = new Date(data.value)
  if (pipeformat) {
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
  }
  // is it a valid date? If entered year is less than 1000 consider it invalid.
  let yearLength =
    date === 'Invalid Date' ? null : date.getFullYear().toString().length
  if (!testFormat(data.value) || !isValid(date) || yearLength !== 4) {
    return ERRORS.invalid
  }
  // is it later than the max date?
  if (data && data.maxDate && isAfter(date, endOfDay(data.maxDate))) {
    return ERRORS.maxDate
  }
  // is it earlier than the min date?
  if (data && data.minDate && isBefore(date, startOfDay(data.minDate))) {
    return ERRORS.minDate
  }
  // is it a blackout date?
  if (Array.isArray(data.blackoutDates)) {
    for (let i = 0; i < data.blackoutDates.length; i++) {
      if (isSameDay(data.blackoutDates[i], date)) {
        return ERRORS.blackoutDate
      }
    }
  }
  return ''
}
// This function is used only for checking custom format
// If you want local formating support along with custom format then please use the dateTime function from the dateTime.js file
function isValidCustomDateFormat(value, formatStr) {
  var dateStr = ensureDateString(value).replace(
    /(?:\+|-)(?:\d{4}|\d{2}:\d{2})$/,
    ''
  )
  if (formatStr) {
    var formattedValue = format(
      dateStr,
      FORMATS[formatStr.replace(/_NOTZ/, '')] || formatStr
    )
  }

  if (formattedValue === 'Invalid Date') return ''
  return formattedValue
}

function isValidRange(data, pattern) {
  let dates = data.value.filter(Boolean)
  let dateError = ''
  // are there at least 2 dates?
  if (dates.length < 2) return ERRORS.required
  // are there any invalid dates?
  dateError = dates.reduce(function (result, date) {
    return (
      isValidDate(Object.assign({}, data, { value: date }), pattern) || result
    )
  }, '')
  if (dateError) return dateError
  // is the range out of order?
  if (isBefore(dates[1], dates[0])) return ERRORS.dateRangeOrder
  return ''
}

function validate(pattern, pipeformat) {
  return forwardError(function validate(data) {
    return Array.isArray(data.value)
      ? isValidRange(data, pattern)
      : isValidDate(data, pattern, pipeformat)
  })
}

function getRawValue(pattern, formatter) {
  return function convertToRaw(value) {
    let formattedValue =
      typeof formatter === 'function' ? formatter(value) : value
    return isValidFormat(pattern)(formattedValue)
      ? format(formattedValue, 'YYYY-MM-DDTHH:mm:ss')
      : value
  }
}

const littleToISO = (value = '') => {
  value = typeof value === 'object' ? format(value) : value
  // preventing ISO formatted string from reverse because it was causing issue for patterns.iso formatted dates ( Ex: "2020-01-01T00:00:00" changed to "01T00:00:00-01-2020" )
  if (value.indexOf('T') > -1) return value
  return value
    .split(/[\/\-]/)
    .reverse()
    .join('-')
}

function getPlaceholder(defaultFormat) {
  return function getLocalePlaceholder() {
    return PLACEHOLDERS[i18nUtils.getLocale()] || defaultFormat
  }
}

const getMiddleEndian = function (config) {
  config = config || {}
  let separator = config.hasOwnProperty('separator') ? config.separator : '/'
  let pipeFormat = 'mm' + separator + 'dd' + separator + 'yyyy'
  var calendarDayFormat = config.calendarDayFormat || 'dd'
  return {
    mask: maskFromString('##' + separator + '##' + separator + '####'),
    format: 'MM' + separator + 'DD' + separator + 'YYYY',
    getPlaceholder: getPlaceholder(pipeFormat),
    calendarDayFormat: calendarDayFormat,
    isValidFormat: isValidFormat(),
    pipeFormat: pipeFormat,
    getRawValue: getRawValue(),
    validate: pipe(checkRequired, validate()),
  }
}

const getLittleEndian = config => {
  config = config || {}
  const separator = config.hasOwnProperty('separator') ? config.separator : '/'
  const pipeFormat = 'dd' + separator + 'mm' + separator + 'yyyy'
  const format = 'DD' + separator + 'MM' + separator + 'YYYY'
  var calendarDayFormat = config.calendarDayFormat || 'dd'
  return {
    mask: maskFromString('##' + separator + '##' + separator + '####'),
    format,
    getPlaceholder: getPlaceholder(pipeFormat),
    isValidFormat: isValidFormat(patterns.iso),
    pipeFormat,
    calendarDayFormat: calendarDayFormat,
    getRawValue: getRawValue(patterns.iso, littleToISO),
    validate: pipe(
      data =>
        Object.assign({}, data, {
          value: Array.isArray(data.value)
            ? data.value
            : littleToISO(data.value),
        }),
      checkRequired,
      validate(patterns.iso, pipeFormat)
    ),
  }
}

/**
 * Returns a custom date utils config object for provided config properties
 * @param {string} [separator] The separator used in date
 * @param {string} [maskString] The mask string as required
 * @param {string} [format] The format of the date
 * @param {string} [pipeFormat] The pipe format of the date
 * @param {string} [calendarDayFormat] The calendar day format of the date
 * @returns {object} The date utils config object
 */
const getDateUtilsConfig = config => {
  config = config || {}
  const separator = config.hasOwnProperty('separator') ? config.separator : '/'
  const mask = config.maskString
    ? maskFromString(config.maskString)
    : maskFromString('##' + separator + '##' + separator + '####')
  const format = config.format || 'DD' + separator + 'MM' + separator + 'YYYY'
  const pipeFormat =
    config.pipeFormat || 'dd' + separator + 'mm' + separator + 'yyyy'
  const calendarDayFormat = config.calendarDayFormat || 'dd'
  return {
    mask,
    format,
    getPlaceholder: getPlaceholder(pipeFormat),
    isValidFormat: isValidFormat(patterns.iso),
    pipeFormat,
    calendarDayFormat: calendarDayFormat,
    getRawValue: getRawValue(patterns.iso, littleToISO),
    validate: pipe(
      data => Object.assign({}, data, { value: littleToISO(data.value) }),
      checkRequired,
      validate(patterns.iso, pipeFormat)
    ),
  }
}

let ISO8601 = {
  mask: maskFromString('####-##-##'),
  format: 'YYYY-MM-DD',
  getPlaceholder: getPlaceholder('yyyy-mm-dd'),
  isValidFormat: isValidFormat(patterns.iso),
  pipeFormat: 'yyyy-mm-dd',
  getRawValue: getRawValue(patterns.iso),
  validate: pipe(checkRequired, validate(patterns.iso, 'yyyy-mm-dd')),
}

let utils = {
  'en-CA': ISO8601,
  'en-GB': getLittleEndian(),
  'en-IE': getLittleEndian(),
  'en-US': getMiddleEndian(),
  'es-CL': getLittleEndian({
    separator: '-',
  }),
  'pt-BR': getLittleEndian({ calendarDayFormat: 'ddd' }),
  'es-ES': getLittleEndian(),
  'es-MX': getLittleEndian(),
  'fr-FR': getLittleEndian(),
  'hi-IN': getLittleEndian({ separator: '-' }),
  'zh-CN': ISO8601,
  'es-US': ISO8601,
  'fr-CA': ISO8601,
}

// named exports (mostly for testing)
exports.patterns = patterns
exports.isValidDate = isValidDate
exports.isValidRange = isValidRange
exports.getPlaceholder = getPlaceholder
exports.getRawValue = getRawValue
exports.littleToISO = littleToISO
exports.validate = validate
exports.utils = utils
exports.getDateUtilsConfig = getDateUtilsConfig
exports.isValidCustomDateFormat = isValidCustomDateFormat

// util export
exports.getByLocale = config => {
  // manual locale passing (useful for i18n inputs)
  if (typeof config === 'string') {
    return utils[config] || {}
  } else if (!isObject(config)) {
    return {}
  }
  return utils[config.locale] || {}
}
